import {
  __ONLY_WRITE_STATE_FROM_ACTIONS as writeState,
  readState,
} from "@/__main__/app-state.mjs";
import { isPersistent, isVolatile, MAX_TIME } from "@/__main__/constants.mjs";
import createModel, { Optional } from "@/__main__/data-model.mjs";
import getData from "@/__main__/get-data.mjs";
import blitzMessage, { EVENTS } from "@/__main__/ipc-core.mjs";
import { setVolatileKV, writeSettings } from "@/app/actions.mjs";
import {
  getLatestMacRelease,
  getLatestWindowsRelease,
} from "@/hub-settings/utils.mjs";
import clone from "@/util/clone.mjs";
import { devError } from "@/util/dev.mjs";

export async function setOpenAtLogin(value) {
  try {
    await blitzMessage(
      value ? EVENTS.ENABLE_AUTOLAUNCH : EVENTS.DISABLE_AUTOLAUNCH,
    );
  } catch (error) {
    devError("Failed to sync settings from core", error);
  } finally {
    await syncLoginItemSettings();
  }
}

const loginItemSettingsModel = createModel({
  executableWillLaunchAtLogin: Boolean,
  launchItems: [String],
  openAsHidden: Boolean,
  openAtLogin: Boolean,
  restoreState: Boolean,
  wasOpenedAsHidden: Boolean,
  wasOpenedAtLogin: Boolean,
});

export async function syncLoginItemSettings() {
  await getData(
    async () => {
      const result = await blitzMessage(EVENTS.GET_LOGIN_ITEM_SETTINGS);
      if (result) result[isVolatile] = true;
      return result;
    },
    loginItemSettingsModel,
    ["volatile", "loginItemSettings"],
    {
      shouldSkipDBRead: true,
      skipSafetyCheck: true,
    },
  );
}

export async function setHardwareAcceleration(value) {
  try {
    await blitzMessage(EVENTS.TOGGLE_HWA, value);
  } catch (error) {
    devError("Failed to sync settings from core", error);
  } finally {
    await syncHardwareAcceleration();
  }
}

const hwModel = createModel({
  enabled: Optional(Boolean),
});

export async function syncHardwareAcceleration() {
  await getData(
    async () => {
      return {
        enabled: await blitzMessage(EVENTS.TOGGLE_HWA),
        [isVolatile]: true,
      };
    },
    hwModel,
    ["volatile", "hardwareAcceleration"],
    {
      shouldSkipDBRead: true,
      skipSafetyCheck: true,
    },
  );
}

export async function setMinimizeBlitzToTray(value) {
  try {
    await blitzMessage(EVENTS.APP_FORCE_CLOSE_SETTING, value);
  } catch (error) {
    devError("Failed to sync settings from core", error);
  } finally {
    await syncMinimizeBlitzToTray();
  }
}

const minimizeBlitzToTrayModel = createModel({
  enabled: Boolean,
});

export async function syncMinimizeBlitzToTray() {
  await getData(
    async () => {
      // TODO: we should refactor this to a primitive symbol when getData
      // is updated to handle primitives.
      // blitz core will minimize the app when appForceClose is undefined, so fallback to "true"
      const forceAppToClose =
        (await blitzMessage(EVENTS.DB_READ, "appForceClose")) ?? "true";

      return {
        enabled: forceAppToClose === "true",
        [isVolatile]: true,
      };
    },
    minimizeBlitzToTrayModel,
    ["volatile", "minimizeBlitzToTray"],
    {
      shouldSkipDBRead: true,
      skipSafetyCheck: true,
    },
  );
}

const versionModel = createModel({
  value: String,
});

// This only needs to be done exactly once.
let hasSyncedAppVersion = false;
export async function syncAppVersion() {
  if (hasSyncedAppVersion) return;
  await getData(
    async () => {
      // TODO: we should refactor this to a primitive symbol when getData
      // is updated to handle primitives.
      return {
        value: await blitzMessage(EVENTS.APP_VERSION),
        [isVolatile]: true,
      };
    },
    versionModel,
    ["volatile", "appVersion"],
    {
      shouldSkipDBRead: true,
      skipSafetyCheck: true,
    },
  );
  hasSyncedAppVersion = true;
}

export async function syncLatestWindowsRelease() {
  await getData(
    async () => {
      // TODO: we should refactor this to a primitive symbol when getData
      // is updated to handle primitives.
      return {
        value: await getLatestWindowsRelease(),
        [isVolatile]: true,
      };
    },
    versionModel,
    ["volatile", "latestWindowsRelease"],
    {
      shouldSkipDBRead: true,
      skipSafetyCheck: true,
    },
  );
}

export async function syncLatestMacRelease() {
  await getData(
    async () => {
      // TODO: we should refactor this to a primitive symbol when getData
      // is updated to handle primitives.
      return {
        value: await getLatestMacRelease(),
        [isVolatile]: true,
      };
    },
    versionModel,
    ["volatile", "latestMacRelease"],
    {
      shouldSkipDBRead: true,
      skipSafetyCheck: true,
    },
  );
}

export async function checkForUpdates() {
  try {
    await blitzMessage(EVENTS.CHECK_FOR_UPDATES);
  } catch (error) {
    devError("Failed to sync settings from core", error);
  }
}

export async function restartApp() {
  try {
    await blitzMessage(EVENTS.APP_RESTART);
  } catch (error) {
    devError("Failed to sync settings from core", error);
  }
}

export async function resetSessionData() {
  try {
    await blitzMessage(EVENTS.APP_RESET_SESSION_DATA);
  } catch (error) {
    devError("Failed to sync settings from core", error);
  }
}

export function updateTftSkinSettings(boolean) {
  if (readState.settings.tft.isSkinsEnabled === boolean) return;
  writeSettings(["tft", "isSkinsEnabled"], boolean);
}

export function updateActiveSessions(sessionTrackerId, isCurrentSession) {
  const user = clone(readState.user);
  let sessions;
  if (isCurrentSession) {
    sessions = user.activeSessions.filter(
      (session) => session.sessionTrackerId === sessionTrackerId,
    );
  } else {
    sessions = user.activeSessions.filter(
      (session) => session.sessionTrackerId !== sessionTrackerId,
    );
  }
  user.activeSessions = sessions;
  user[isPersistent] = MAX_TIME;
  writeState.user = user;
}

export function setShowManageAccountModal(boolean) {
  setVolatileKV("showManageAccountModal", boolean);
}
